import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useContent } from "@/context/ContentProvider";
import { formatPhone } from "@/utils/phone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again or call us.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
}

export default function ConsultationForm({ formId = "consultation", onSuccess }) {
  const { services } = useContent();
  const empty = { name: "", phone: "", email: "", service: "", message: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: form.email || undefined,
          service: form.service || undefined,
          message: form.message || undefined,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(formatDetail(data?.detail));
      }
      toast.success("Request received — we'll call you to schedule your free in-home consultation.");
      setForm(empty);
      onSuccess?.();
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please call us at 440-381-7828.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${formId}-form`}>
      <div className="grid sm:grid-cols-2 gap-4">
        <input required className="input-dark" placeholder="Full name *" value={form.name} onChange={set("name")} data-testid={`${formId}-name-input`} />
        <input required className="input-dark" placeholder="Phone *" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })} data-testid={`${formId}-phone-input`} />
      </div>
      <input className="input-dark" placeholder="Email (optional)" type="email" value={form.email} onChange={set("email")} data-testid={`${formId}-email-input`} />
      <select className="input-dark" value={form.service} onChange={set("service")} data-testid={`${formId}-service-select`}>
        <option value="">Which space are we transforming?</option>
        {services.map((s) => (
          <option key={s.slug} value={s.name}>{s.name}</option>
        ))}
        <option value="Not sure yet">Not sure yet</option>
      </select>
      <textarea className="input-dark min-h-[96px] resize-y" placeholder="Tell us about your project (optional)" value={form.message} onChange={set("message")} data-testid={`${formId}-message-input`} />
      <button
        type="submit"
        disabled={loading}
        data-testid={`${formId}-submit-button`}
        className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold tracking-wide uppercase text-sm py-4 rounded-md shadow-md transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {loading ? "Sending..." : "Schedule My Free Consultation"}
      </button>
      <p className="text-xs text-stone-500 text-center">Free in-home consultations across Cleveland &amp; Northeast Ohio.</p>
    </form>
  );
}
